import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SlideOver from 'components/shared/SlideOver';
import UserWorkflowInstanceView from 'components/workflows/UserWorkflowInstanceView';
import { ApiService } from 'services/ApiService';
import { Team } from 'models/teams/Team';
import { utilities } from 'utils/utilities';
import type { WorkflowInstanceSummaryDto } from 'models/workflows/WorkflowInstanceSummaryDto';
import type { UserWorkflowInstanceDetailDto } from 'models/workflows/UserWorkflowInstanceDetailDto';

interface FollowedArtist {
  artistUserID: string;
  artistUserName: string;
  location?: string;
}

const formatStaleness = (days: number, hours: number): string => {
  if (days <= 0 && hours <= 0) return 'just now';
  if (days <= 0) return `${hours}h ago`;
  return `${days}d ${hours}h ago`;
};

const statusBadgeClass = (status: string, isArchived: boolean): string => {
  if (isArchived) return 'bg-gray-100 text-gray-700';
  switch (status) {
    case 'Completed':
      return 'bg-green-100 text-green-800';
    case 'Cancelled':
    case 'Failed':
      return 'bg-red-100 text-red-800';
    case 'InProgress':
    default:
      return 'bg-blue-100 text-blue-800';
  }
};

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500',
  'bg-indigo-500', 'bg-teal-500', 'bg-orange-500', 'bg-red-500',
];

const getInitials = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase() || '?';
};

const getAvatarColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const ArtistAvatar: React.FC<{
  artistId: string | null;
  artistProfilePictureId: string | null;
  artistName: string;
  size?: 'sm' | 'md';
}> = ({ artistId, artistProfilePictureId, artistName, size = 'sm' }) => {
  const [imgError, setImgError] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const sizeClass = size === 'md' ? 'w-8 h-8' : 'w-6 h-6';
  const textSize = size === 'md' ? 'text-xs' : 'text-[10px]';

  const imgSrc = artistId && artistProfilePictureId && !imgError
    ? `${API_BASE_URL}/artists/${artistId}/ArtistImages/${artistProfilePictureId}`
    : null;

  if (imgSrc) {
    return (
      <img
        src={imgSrc}
        alt={artistName}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <span
      className={`${sizeClass} rounded-full ${getAvatarColor(artistName)} text-white flex items-center justify-center flex-shrink-0 ${textSize} font-semibold`}
      aria-label={artistName}
    >
      {getInitials(artistName)}
    </span>
  );
};

const pendingLabel = (
  pendingEnrollmentType: string | null,
  status: string,
): string => {
  if (status === 'Completed') return 'Completed';
  if (status === 'Cancelled') return 'Cancelled';
  if (status === 'Failed') return 'Failed';
  switch (pendingEnrollmentType) {
    case 'FormEnrollment':
      return 'Waiting on you';
    case 'ReviewEnrollment':
      return 'In team review';
    case 'EventEnrollment':
      return 'Awaiting scheduled event';
    default:
      return 'In progress';
  }
};

const UserProfilePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workflowInstances, setWorkflowInstances] = useState<WorkflowInstanceSummaryDto[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [followedArtists, setFollowedArtists] = useState<FollowedArtist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(
    () => searchParams.get('workflowInstanceId'),
  );
  const [selectedDetail, setSelectedDetail] = useState<UserWorkflowInstanceDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailSubmitting, setDetailSubmitting] = useState(false);

  const userId = utilities.getUserId();

  // Auto-open the workflow instance the customer was returned to (e.g. after Stripe deposit
  // checkout). Strip the query param once consumed so a later refresh/share of the URL
  // doesn't keep forcing the slide-over open.
  useEffect(() => {
    if (searchParams.get('workflowInstanceId')) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWorkflowInstances = useCallback(async () => {
    const paged = await ApiService.getMyWorkflowInstances({ pageSize: 50 });
    setWorkflowInstances(paged.items ?? []);
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);

      try {
        await fetchWorkflowInstances();
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        setError('Failed to load your dashboard. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId, fetchWorkflowInstances]);

  useEffect(() => {
    if (!selectedInstanceId) {
      setSelectedDetail(null);
      setDetailError(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    ApiService.getMyWorkflowInstance(selectedInstanceId)
      .then((detail) => { if (!cancelled) setSelectedDetail(detail); })
      .catch(() => { if (!cancelled) setDetailError('Failed to load project details. Please try again.'); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });

    return () => { cancelled = true; };
  }, [selectedInstanceId]);

  const refreshSelectedProject = useCallback(async () => {
    if (!selectedInstanceId) return;
    const detail = await ApiService.getMyWorkflowInstance(selectedInstanceId);
    setSelectedDetail(detail);
    try {
      await fetchWorkflowInstances();
    } catch (err) {
      console.error('Failed to refresh workflow instance list:', err);
    }
  }, [selectedInstanceId, fetchWorkflowInstances]);

  useEffect(() => {
    const fetchTeams = async () => {
      setLoading(true);
      setError(null);

      try {
        const teamsResponse = await ApiService.getUserTeams(utilities.getUserId())
        if (teamsResponse.success && teamsResponse.data) setTeams(teamsResponse.data);
      } catch (err) {
        console.error('Failed to fetch teams:', err);
        setError('Failed to load your teams. Please try again.');
      }
    }
    fetchTeams();
  }, [userId]);

  useEffect(() => {
    const fetchArtistsFollowing = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await ApiService.getArtistsFollowing(userId);
        if (response.success && response.data) {
          setFollowedArtists(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch your followed artists:', err);
        setError('Failed to load your followed artists. Please try again.');
      }
    }
    fetchArtistsFollowing();
  }, [userId]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">My Dashboard</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Workflow Instances Section */}
        <section className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-gray-900">My Projects</h2>
            <span className="text-sm text-gray-500">{workflowInstances.length} total</span>
          </div>

          {workflowInstances.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-600 mb-4">You don't have any projects yet.</p>
              <p className="text-sm text-gray-500">
                Projects will appear here when you start working with artists.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {workflowInstances.map(instance => {
                const title = instance.projectName || instance.templateName;
                const showSubtitle = Boolean(instance.projectName) && instance.projectName !== instance.templateName;
                const meta = pendingLabel(instance.pendingEnrollmentType, instance.status);
                return (
                  <button
                    type="button"
                    key={instance.workflowInstanceId}
                    onClick={() => setSelectedInstanceId(instance.workflowInstanceId)}
                    className="block w-full text-left bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-shadow"
                    aria-haspopup="dialog"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {title}
                        </h3>
                        {showSubtitle && (
                          <p className="text-sm text-gray-600 mb-3">{instance.templateName}</p>
                        )}
                        <p className="text-sm text-gray-600 mb-3">
                          <span className="inline-flex items-center gap-1.5">
                            <ArtistAvatar
                              artistId={instance.artistId}
                              artistProfilePictureId={instance.artistProfilePictureId}
                              artistName={instance.artistName}
                            />
                            {instance.artistName || 'Unknown artist'}
                          </span>
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>{meta}</span>
                          <span>•</span>
                          <span>
                            Last update {formatStaleness(instance.daysSinceLastInput, instance.hoursSinceLastInput)}
                          </span>
                          {instance.isArchived && (
                            <>
                              <span>•</span>
                              <span className="italic">Archived</span>
                            </>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadgeClass(instance.status, instance.isArchived)}`}>
                        {instance.status}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Sidebar */}
        <aside className="space-y-8">
          {/* Teams Section */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">My Teams (Member of)</h2>
            {teams.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-sm text-gray-600">No teams yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teams.map(team => (
                  <Link
                  key={team.teamId}
                  to={`/artist/${team.artistId}/operations`}
                  className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div
                    key={team.teamId}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <h3 className="font-medium text-gray-900">{team.teamName}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {team.teammates?.length || 1} members
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

          {/* Followed Artists Section */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Following</h2>
            {followedArtists.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-sm text-gray-600 mb-3">
                  Not following any artists yet.
                </p>
                <Link
                  to="/home"
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Discover artists
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {followedArtists.map(artist => (
                  <Link
                    key={artist.artistUserID}
                    to={`/artist/${artist.artistUserID}`}
                    className="block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                  >
                    <h3 className="font-medium text-gray-900">{artist.artistUserName}</h3>
                    {artist.location && (
                      <p className="text-sm text-gray-500 mt-1">{artist.location}</p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </section>
        </aside>
      </div>

      <SlideOver
        isOpen={selectedInstanceId !== null}
        onClose={() => setSelectedInstanceId(null)}
        title={
          <div className="min-w-0">
            <p className="truncate">
              {selectedDetail?.projectName || selectedDetail?.templateName || 'Project details'}
            </p>
            {selectedDetail?.artistName && (
              <p className="mt-0.5 truncate text-xs font-normal text-gray-500">
                <span className="inline-flex items-center gap-1">
                  <ArtistAvatar
                    artistId={selectedDetail.artistId ?? null}
                    artistProfilePictureId={selectedDetail.artistProfilePictureId ?? null}
                    artistName={selectedDetail.artistName}
                  />
                  {selectedDetail.artistName}
                </span>
              </p>
            )}
          </div>
        }
        width="w-[680px]"
      >
        {detailLoading && (
          <div className="space-y-3 p-5" aria-label="Loading project details">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
            ))}
          </div>
        )}
        {detailError && (
          <p role="alert" className="p-5 text-sm text-red-600">{detailError}</p>
        )}
        {!detailLoading && !detailError && selectedDetail && (
          <UserWorkflowInstanceView
            detail={selectedDetail}
            isSubmitting={detailSubmitting}
            onSubmittingChange={setDetailSubmitting}
            onSubmitted={refreshSelectedProject}
          />
        )}
      </SlideOver>
    </div>
  );
};

export default UserProfilePage;
